import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import AnimatedBackground from "@/components/AnimatedBackground";
import CustomCursor from "@/components/CustomCursor";
import { RequireAuth } from "@/components/admin/RequireAuth";
import { logPageView } from "@/lib/firebase";

// Admin is only ever visited by the site owner — keep it out of the public bundle.
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const Admin = lazy(() => import("./pages/Admin"));
// A separate subpage, not part of the homepage's initial render — split out too.
const Templates = lazy(() => import("./pages/Templates"));
const MobileAppPrivacyPolicy = lazy(() => import("./pages/MobileAppPrivacyPolicy"));
const ServicesHub = lazy(() => import("./pages/ServicesHub"));
const ServicePage = lazy(() => import("./pages/ServicePage"));

// Firebase Analytics doesn't auto-track navigation in a client-side-only
// SPA the way it would on a traditional multi-page site — each route change
// has to be logged as its own page_view explicitly. Skips /admin so the
// site owner's own visits don't skew visitor analytics.
const AnalyticsTracker = () => {
  const location = useLocation();
  useEffect(() => {
    if (location.pathname.startsWith('/admin')) return;
    logPageView(location.pathname);
  }, [location.pathname]);
  return null;
};

// The moving WebGL net behind everything else is a homepage flourish — on a
// privacy policy page it just competes with the legal text for attention, so
// skip mounting it there entirely (not just visually hiding it).
const RouteAwareBackground = () => {
  const location = useLocation();
  if (location.pathname.startsWith('/mobileapps/')) return null;
  return <AnimatedBackground />;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A missing table/resource (404) won't succeed on retry — don't burn
      // 3 exponential-backoff attempts (and stretch the load waterfall) on it.
      retry: 1,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <CustomCursor />
      <BrowserRouter>
        <AnalyticsTracker />
        <RouteAwareBackground />
        <Routes>
          <Route path="/" element={<Index />} />
          {/* A project's own shareable, crawlable URL — same page, deep-linked to that project's modal */}
          <Route path="/projects/:slug" element={<Index />} />
          <Route
            path="/templates"
            element={
              <Suspense fallback={null}>
                <Templates />
              </Suspense>
            }
          />
          {/* Keyword-targeted service landing pages — prerendered per URL by scripts/prerender-pages.mjs */}
          <Route
            path="/services"
            element={
              <Suspense fallback={null}>
                <ServicesHub />
              </Suspense>
            }
          />
          <Route
            path="/services/:slug"
            element={
              <Suspense fallback={null}>
                <ServicePage />
              </Suspense>
            }
          />
          <Route
            path="/mobileapps/:slug/privacypolicy"
            element={
              <Suspense fallback={null}>
                <MobileAppPrivacyPolicy />
              </Suspense>
            }
          />
          <Route
            path="/admin/login"
            element={
              <Suspense fallback={null}>
                <AdminLogin />
              </Suspense>
            }
          />
          <Route
            path="/admin"
            element={
              <Suspense fallback={null}>
                <RequireAuth>
                  <Admin />
                </RequireAuth>
              </Suspense>
            }
          />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
